import { equal } from '@ember/object/computed';
import { assert } from '@ember/debug';
import { computed, set } from '@ember/object';
import Enum from '@square/dashboard-utilities/utils/enum';
import DateTime from '@square/dashboard-data/models/squareup/common/time/date-time';
import Entity from '@square/dashboard-data/models/squareup/sub2/entity';
import Usage from '@square/dashboard-data/models/squareup/sub2/usage';
import { BillingCycle } from '@square/dashboard-data/models/squareup/sub2/external-plan';
import Duration from '@square/dashboard-data/models/squareup/sub2/duration';
import Money from '@square/dashboard-data/models/squareup/common/money-proto';
import Pricing from '@square/dashboard-data/models/squareup/sub2/pricing';
import PlanChange from '@square/dashboard-data/models/squareup/sub2/plan-change';
import Feature from '@square/dashboard-utilities/utils/sub2/feature';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import { underscore } from '@square/dashboard-utilities/utils/keys';
export const Status = new Enum(['INVALID', 'PENDING', 'FREE_TRIAL', 'FREE_TRIAL_PENDING_CANCELLATION', 'ACTIVE', 'ACTIVE_PENDING_CANCELLATION', 'CANCELED', 'DELINQUENT', 'IN_GRACE', 'FREE_TRIAL_NO_OBLIGATION', 'NO_OBLIGATION_FREE_TRIAL_EXPIRED', 'ACTIVE_PENDING_PAUSE', 'PAUSED', 'PRESALE']);

// DO NOT USE THIS LIST. Use ENABLED_FEATURE_STATUSES in
// frontend/dashboard/app/utils/sub2-subscriptions.js instead.
//
// SUBSCRIBED_WITHOUT_PENDING_ACTION was originally defined as part of a task to prompt team
// management subscribers to re-subscribe when their team management subscription was
// canceled or pending cancellation (see PRICES-1686). This list is still actively used in
// frontend/dashboard/app/services/employee-subscription.js to feed into a computed property
// (hasActivelySubscribedEmployeeManagementFeature) that is used many times in team management code.
//
// This list is also used to hide pending cancellation subscriptions if their feature set is a
// strict subset of features from subscriptions whose status is in this list (see SUBS-489).
// It also powers the "isSubscribedWithoutPendingAction" computed property on the subscription
// model, but this property is also only used in the code added for SUBS-489.
//
// A single subscription status should only appear once in either SUBSCRIBED_WITHOUT_PENDING_ACTION
// or CAN_SUBSCRIBE_STATUSES.
// TODO(SUBS-1180) - Eliminate dependencies on this list.
export const SUBSCRIBED_WITHOUT_PENDING_ACTION = [Status.ACTIVE, Status.FREE_TRIAL, Status.IN_GRACE, Status.FREE_TRIAL_NO_OBLIGATION, Status.NO_OBLIGATION_FREE_TRIAL_EXPIRED];

// DO NOT USE THIS LIST. Use ENABLED_FEATURE_STATUSES in
// frontend/dashboard/app/utils/sub2-subscriptions.js instead.
//
// CAN_SUBSCRIBE_STATUSES was originally defined as part of a task to prompt team
// management subscribers to re-subscribe when their team management subscription was
// canceled or pending cancellation (see PRICES-1686). This list is still actively used in
// frontend/dashboard/app/services/employee-subscription.js to feed into a computed property
// (shouldPromptForResubscription) that is only used in a single place in team management code.
//
// A single subscription status should only appear once in either SUBSCRIBED_WITHOUT_PENDING_ACTION
// or CAN_SUBSCRIBE_STATUSES.
// TODO(SUBS-1180) - Eliminate dependencies on this list.
export const CAN_SUBSCRIBE_STATUSES = [Status.FREE_TRIAL_PENDING_CANCELLATION, Status.ACTIVE_PENDING_CANCELLATION, Status.CANCELED, Status.PENDING, Status.DELINQUENT];

// PENDING_CANCELLATION_STATUSES was originally added to hide pending cancellation subscriptions if
// their feature set is a strict subset of features from subscriptions whose status is in this list
// (see SUBS-489). It also powers the "isPendingCancellation" computed property on the subscription
// model, but this property is also only used in the code added for SUBS-489.
// TODO(SUBS-1180) - Eliminate dependencies on this list and delete it afterwards.
export const PENDING_CANCELLATION_STATUSES = [Status.ACTIVE_PENDING_CANCELLATION, Status.FREE_TRIAL_PENDING_CANCELLATION, Status.NO_OBLIGATION_FREE_TRIAL_EXPIRED, Status.FREE_TRIAL_NO_OBLIGATION];
const BillingStatus = new Enum(['NONE', 'BILLED', 'PAID', 'FAILED']);
export const ManagedBy = new Enum(['UNKNOWN', 'SQUARE', 'APPLE', 'GOOGLE']);
export const EXTERNAL_SUBSCRIPTION_MANAGEMENT_LINKS = {
  payroll_for_employees: '/payroll/employer/settings/payroll-settings',
  payroll_for_employees_with_free_trials: '/payroll/employer/settings/payroll-settings',
  payroll_for_employees_with_free_trials_v2: '/payroll/employer/settings/payroll-settings',
  payroll_for_contractors: '/payroll/employer/settings/payroll-settings'
};
export default ProtoBaseModel.extend({
  token: attr(String),
  subscriberEntity: attr(Entity),
  planToken: attr(String),
  productOwnerEntity: attr(Entity),
  status: attr(Status),
  billingStatus: attr(BillingStatus),
  startAt: attr(DateTime),
  endAt: attr(DateTime),
  delinquentAt: attr(DateTime),
  renewalAt: attr(DateTime),
  planName: attr(String),
  freeTrialEndAt: attr(DateTime),
  nextFeeAmount: attr(Money),
  currentUsage: attr(Usage),
  renewalCycle: attr(Duration),
  pricing: attr(Pricing),
  nextFeeDate: attr(DateTime),
  currentCost: attr(Money),
  features: repeated(Feature),
  resumeAt: attr(DateTime),
  free: attr(Boolean, {
    defaultValue: false
  }),
  pendingDowngrade: attr(PlanChange),
  lastPlanChange: attr(PlanChange),
  planGroupToken: attr(String),
  planBillingCycle: attr(Number),
  bundleToken: attr(String),
  managedBy: attr(ManagedBy),
  canPause: attr(Boolean),
  canCancel: attr(Boolean),
  hasFeatures: computed('features', function () {
    const features = this.features;
    return features && features.length > 0;
  }),
  isPendingCancellation: computed('status', function () {
    return PENDING_CANCELLATION_STATUSES.includes(this.status);
  }),
  isCancelled: computed('status', function () {
    return this.status === Status.CANCELED;
  }),
  // TODO(SUBS-1180) - Eliminate dependencies on this property and delete it afterwards.
  isSubscribedWithoutPendingAction: computed('status', function () {
    return SUBSCRIBED_WITHOUT_PENDING_ACTION.includes(this.status);
  }),
  // DO NOT USE THIS ALIAS.
  // It is only here in case there are existing dependencies on this property that not yet been
  // eliminated.
  // TODO(SUBS-1180): Delete this alias if there are no dependencies on it.
  isActive: computed('isSubscribedWithoutPendingAction', function () {
    assert('"isActive" is deprecated and will soon be deleted. Do not use this.');
    return this.isSubscribedWithoutPendingAction;
  }),
  // TODO (nrobinson): We can remove this after May 1, 2020 and move back to just using nextFeeDate.
  // We need this attribute as we are not charging customers for subscriptions for April/May 2020.
  adjustedNextFeeDate: computed('paused', 'renewalAt', function () {
    const END_OF_FREE_COVID_19_SUBSCRIPTIONS = new Date(1591012800000); // Monday, June 1, 2020
    return this.renewalAt < END_OF_FREE_COVID_19_SUBSCRIPTIONS ? END_OF_FREE_COVID_19_SUBSCRIPTIONS : this.renewalAt;
  }),
  isPendingPause: equal('status', 'ACTIVE_PENDING_PAUSE'),
  isPaused: equal('status', 'PAUSED'),
  externalManagementLink: computed('_externalManagementLink', 'planToken', {
    get() {
      if (this._externalManagementLink !== undefined) {
        return this._externalManagementLink;
      }
      return EXTERNAL_SUBSCRIPTION_MANAGEMENT_LINKS[this.planToken];
    },
    set(_, value) {
      return set(this, '_externalManagementLink', value);
    }
  })
}).reopenClass({
  // Map from sub2 subscription proto in @square/dashboard-protos/models/squareup/sub2/data.
  fromProto(proto) {
    if (!proto) {
      return null;
    }
    return this.deserialize(underscore(proto.toJSON()));
  },
  deserialize(data) {
    let transformedData = data;
    if (data) {
      transformedData = {
        ...data
      };
      if (typeof transformedData.plan_billing_cycle === 'string') {
        transformedData.plan_billing_cycle = BillingCycle.indexOf(transformedData.plan_billing_cycle);
      }
    }
    return this._super(transformedData);
  }
});